import React from 'react';
import './TextField.css';

interface TextFieldProps {
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel';
  disabled?: boolean;
  required?: boolean;
  error?: string;
  className?: string;
  id?: string;
  name?: string;
  maxLength?: number;
  prefixIcon?: React.ReactNode;
  prefixClassName?: string;
  clearable?: boolean;
  onClear?: () => void;
}

export const TextField: React.FC<TextFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  disabled = false,
  required = false,
  error,
  className = '',
  id,
  name,
  maxLength,
  prefixIcon,
  prefixClassName = '',
  clearable = true,
  onClear
}) => {
  const inputId = id || `textfield-${Math.random().toString(36).substr(2, 9)}`;

  const handleClear = () => {
    if (onClear) {
      onClear();
    } else {
      // Create a synthetic event to clear the value
      const syntheticEvent = {
        target: { value: '' }
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    }
  };

  const showClearButton = clearable && value.length > 0 && !disabled;

  return (
    <div className={`textField-container ${className} ${error ? 'textField-error' : ''} ${disabled ? 'textField-disabled' : ''}`}>
      {label && (
        <label htmlFor={inputId} className="textField-label">
          {label}
          {required && <span className="textField-required">*</span>}
        </label>
      )}
      <div className="textField-input-wrapper">
        {prefixIcon && (
          <span className={`textField-prefix-icon ${prefixClassName}`}>
            {prefixIcon}
          </span>
        )}
        <input
          id={inputId}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className="textField-input"
          required={required}
          maxLength={maxLength}
        />
        {showClearButton && (
          <button
            type="button"
            className="textField-clear-btn"
            onClick={handleClear}
            aria-label="Clear input"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        )}
      </div>
      {error && <span className="textField-error-message">{error}</span>}
    </div>
  );
};

export default TextField;
